import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { notifyAdminSecurityBlock } from './notifications';

export const MAX_SECURITY_STRIKES = 3;

export const STRIKE_REASONS = {
  copy: 'Attempted to copy protected content',
  screenshot: 'Screenshot or screen capture detected',
  suspicious: 'Suspicious activity detected',
};

/** Stable device fingerprint for login approval (best-effort in browser). */
export function getDeviceFingerprint() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const scr = typeof screen !== 'undefined' ? screen : {};
  const raw = [
    nav.userAgent || '',
    nav.language || '',
    nav.platform || '',
    scr.width || '',
    scr.height || '',
    scr.colorDepth || '',
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  ].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `dev_${Math.abs(hash).toString(36)}`;
}

export function getDeviceLabel() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone/iPad';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Linux/i.test(ua)) return 'Linux device';
  return 'Unknown device';
}

export async function getStudentSecurityProfile(userId) {
  if (!userId) return null;
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    portalBlocked: Boolean(data.portalBlocked),
    blockReason: data.blockReason || '',
    securityStrikes: Number(data.securityStrikes) || 0,
    approvedDevices: Array.isArray(data.approvedDevices) ? data.approvedDevices : [],
    pendingDeviceId: data.pendingDeviceId || '',
    pendingDeviceLabel: data.pendingDeviceLabel || '',
    displayName: data.displayName || '',
    email: data.email || data.emailLower || '',
  };
}

export async function isDeviceApproved(userId, deviceId) {
  const profile = await getStudentSecurityProfile(userId);
  if (!profile) return true;
  if (profile.portalBlocked) return false;
  if (!profile.approvedDevices.length) return true;
  return profile.approvedDevices.includes(deviceId);
}

export async function requestDeviceApproval(userId, deviceId, deviceLabel) {
  await updateDoc(doc(db, 'users', userId), {
    pendingDeviceId: deviceId,
    pendingDeviceLabel: deviceLabel || getDeviceLabel(),
    pendingDeviceAt: serverTimestamp(),
  });
}

export async function approveStudentDevice(userId, deviceId) {
  await updateDoc(doc(db, 'users', userId), {
    approvedDevices: arrayUnion(deviceId),
    pendingDeviceId: '',
    pendingDeviceLabel: '',
    updatedAt: serverTimestamp(),
  });
}

export async function reactivateStudentPortal(userId) {
  await updateDoc(doc(db, 'users', userId), {
    portalBlocked: false,
    blockReason: '',
    securityStrikes: 0,
    reactivatedAt: serverTimestamp(),
  });
}

export async function blockStudentPortal(userId, reason) {
  const profile = await getStudentSecurityProfile(userId);
  await updateDoc(doc(db, 'users', userId), {
    portalBlocked: true,
    blockReason: reason || 'Unusual activity',
    blockedAt: serverTimestamp(),
  });
  if (profile) {
    await notifyAdminSecurityBlock({
      studentName: profile.displayName,
      email: profile.email,
      userId,
      reason: reason || 'Unusual activity',
    }).catch(console.warn);
  }
}

export async function recordSecurityStrike(userId, reason) {
  const profile = await getStudentSecurityProfile(userId);
  if (!profile || profile.portalBlocked) return { blocked: true, strikes: profile?.securityStrikes || 0 };

  const strikes = (profile.securityStrikes || 0) + 1;
  const updates = {
    securityStrikes: strikes,
    lastSecurityEvent: reason,
    lastSecurityAt: serverTimestamp(),
  };

  if (strikes >= MAX_SECURITY_STRIKES) {
    updates.portalBlocked = true;
    updates.blockReason = reason || STRIKE_REASONS.suspicious;
    updates.blockedAt = serverTimestamp();
  }

  await updateDoc(doc(db, 'users', userId), updates);

  if (strikes >= MAX_SECURITY_STRIKES) {
    await notifyAdminSecurityBlock({
      studentName: profile.displayName,
      email: profile.email,
      userId,
      reason: updates.blockReason,
    }).catch(console.warn);
  }

  return { blocked: strikes >= MAX_SECURITY_STRIKES, strikes };
}
